import { useEffect, useMemo, useState } from 'react';

function BanquetMenuMaster({ token, selectedProperty, selectedOutletId = '' }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId,
    ...(outletId ? { 'x-outlet-id': outletId } : {})
  }), [token, chainId, propertyId, outletId]);

  const [tab, setTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Category form
  const [catForm, setCatForm] = useState({ category_name: '', display_order: 0 });
  const [editingCatId, setEditingCatId] = useState(null);

  // Item form
  const [itemForm, setItemForm] = useState({ item_name: '', category_id: '', food_type: 'VEG', unit_price: '' });
  const [editingItemId, setEditingItemId] = useState(null);
  const [filterCatId, setFilterCatId] = useState('');
  const [filterFoodType, setFilterFoodType] = useState('');

  // Settings state
  const [billingProfiles, setBillingProfiles] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  const [configForm, setConfigForm] = useState({ billing_profile_id: '', tax_id: '' });
  const [configSaving, setConfigSaving] = useState(false);

  // Package state
  const [packages, setPackages] = useState([]);
  const [pkgForm, setPkgForm] = useState({ package_name: '', package_code: '', menu_type: 'VEG', base_price_per_pax: '', description: '' });
  const [pkgRules, setPkgRules] = useState([]);
  const [pkgSelectedItems, setPkgSelectedItems] = useState([]);
  const [editingPkgId, setEditingPkgId] = useState(null);
  const [showPkgBuilder, setShowPkgBuilder] = useState(false);

  const parseJson = async (r) => { const t = await r.text(); try { return JSON.parse(t); } catch { return { error: t }; } };
  const flash = (msg, isErr) => { if (isErr) setError(msg); else setMessage(msg); setTimeout(() => { setMessage(''); setError(''); }, 3000); };

  const loadCategories = async () => {
    const res = await fetch('/api/v1/banquet/menu-categories?all=1', { headers });
    const data = await parseJson(res);
    if (res.ok) setCategories(Array.isArray(data) ? data : []);
  };

  const loadItems = async () => {
    let url = '/api/v1/banquet/menu-items?all=1';
    if (filterCatId) url += `&category_id=${filterCatId}`;
    if (filterFoodType) url += `&food_type=${filterFoodType}`;
    const res = await fetch(url, { headers });
    const data = await parseJson(res);
    if (res.ok) setItems(Array.isArray(data) ? data : []);
  };

  const loadBillingProfiles = async () => {
    try {
      const res = await fetch('/api/v1/pms/billing-profiles?payer_type=SELF', { headers });
      const data = await parseJson(res);
      if (res.ok) setBillingProfiles(Array.isArray(data) ? data : []);
    } catch {}
  };
  const loadTaxOptions = async () => {
    try {
      const res = await fetch('/api/v1/banquet/tax-options', { headers });
      const data = await parseJson(res);
      if (res.ok) setTaxOptions(Array.isArray(data) ? data : []);
    } catch {}
  };
  const loadConfig = async () => {
    try {
      const res = await fetch('/api/v1/banquet/config', { headers });
      const data = await parseJson(res);
      if (res.ok && data) setConfigForm({ billing_profile_id: String(data.billing_profile_id || ''), tax_id: String(data.tax_id || '') });
    } catch {}
  };
  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      const res = await fetch('/api/v1/banquet/config', {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ billing_profile_id: configForm.billing_profile_id ? Number(configForm.billing_profile_id) : null, tax_id: configForm.tax_id ? Number(configForm.tax_id) : null })
      });
      if (res.ok) flash('Settings saved'); else { const d = await parseJson(res); flash(d?.error || 'Failed', true); }
    } catch { flash('Failed to save', true); }
    finally { setConfigSaving(false); }
  };

  const loadPackages = async () => {
    const res = await fetch('/api/v1/banquet/packages?all=1', { headers });
    const data = await parseJson(res);
    if (res.ok) setPackages(Array.isArray(data) ? data : []);
  };

  useEffect(() => { if (chainId && propertyId) { setLoading(true); Promise.all([loadCategories(), loadItems(), loadPackages(), loadBillingProfiles(), loadTaxOptions(), loadConfig()]).finally(() => setLoading(false)); } }, [chainId, propertyId]);
  useEffect(() => { if (chainId && propertyId) loadItems(); }, [filterCatId, filterFoodType]);

  const saveCategory = async () => {
    if (!catForm.category_name.trim()) return flash('Category name is required', true);
    const isEdit = !!editingCatId;
    const res = await fetch(isEdit ? `/api/v1/banquet/menu-categories/${editingCatId}` : '/api/v1/banquet/menu-categories', {
      method: isEdit ? 'PUT' : 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(catForm)
    });
    const data = await parseJson(res);
    if (!res.ok) return flash(data?.error || 'Failed', true);
    flash(isEdit ? 'Category updated' : 'Category created');
    setCatForm({ category_name: '', display_order: 0 }); setEditingCatId(null);
    loadCategories();
  };

  const saveItem = async () => {
    if (!itemForm.item_name.trim() || !itemForm.category_id) return flash('Item name and category are required', true);
    const isEdit = !!editingItemId;
    const res = await fetch(isEdit ? `/api/v1/banquet/menu-items/${editingItemId}` : '/api/v1/banquet/menu-items', {
      method: isEdit ? 'PUT' : 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(itemForm)
    });
    const data = await parseJson(res);
    if (!res.ok) return flash(data?.error || 'Failed', true);
    flash(isEdit ? 'Item updated' : 'Item created');
    setItemForm({ item_name: '', category_id: '', food_type: 'VEG', unit_price: '' }); setEditingItemId(null);
    loadItems();
  };

  const toggleActive = async (type, id, currentActive) => {
    const url = type === 'cat' ? `/api/v1/banquet/menu-categories/${id}` : type === 'pkg' ? `/api/v1/banquet/packages/${id}` : `/api/v1/banquet/menu-items/${id}`;
    await fetch(url, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ is_active: !currentActive }) });
    if (type === 'cat') loadCategories(); else if (type === 'pkg') loadPackages(); else loadItems();
  };

  // Package helpers
  const resetPkgForm = () => { setPkgForm({ package_name: '', package_code: '', menu_type: 'VEG', base_price_per_pax: '', description: '' }); setPkgRules([]); setPkgSelectedItems([]); setEditingPkgId(null); setShowPkgBuilder(false); };

  const editPackage = async (pkgId) => {
    const res = await fetch(`/api/v1/banquet/packages/${pkgId}/full`, { headers });
    const data = await parseJson(res);
    if (!res.ok) return flash(data?.error || 'Failed', true);
    setPkgForm({ package_name: data.package_name, package_code: data.package_code, menu_type: data.menu_type, base_price_per_pax: data.base_price_per_pax, description: data.description || '' });
    setPkgRules((data.rules || []).map(r => ({ category_id: String(r.category_id), min_select: r.min_select, max_select: r.max_select })));
    setPkgSelectedItems((data.items || []).map(i => ({ item_id: String(i.item_id), is_default: i.is_default })));
    setEditingPkgId(pkgId); setShowPkgBuilder(true);
  };

  const savePackage = async () => {
    if (!pkgForm.package_name.trim() || !pkgForm.package_code.trim() || !pkgForm.base_price_per_pax) return flash('Name, code and price/pax required', true);
    const payload = { ...pkgForm, base_price_per_pax: Number(pkgForm.base_price_per_pax), rules: pkgRules.filter(r => r.category_id), items: pkgSelectedItems.filter(i => i.item_id) };
    const isEdit = !!editingPkgId;
    const res = await fetch(isEdit ? `/api/v1/banquet/packages/${editingPkgId}` : '/api/v1/banquet/packages', {
      method: isEdit ? 'PUT' : 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    });
    const data = await parseJson(res);
    if (!res.ok) return flash(data?.error || 'Failed', true);
    flash(isEdit ? 'Package updated' : 'Package created'); resetPkgForm(); loadPackages();
  };

  const pkgFilteredItems = items.filter(i => i.is_active).filter(i => {
    if (pkgForm.menu_type === 'VEG') return i.food_type === 'VEG';
    return true;
  });
  const pkgItemsByCategory = categories.reduce((acc, cat) => {
    acc[cat.category_id] = pkgFilteredItems.filter(i => String(i.category_id) === String(cat.category_id));
    return acc;
  }, {});
  const togglePkgItem = (itemId) => {
    const exists = pkgSelectedItems.find(s => String(s.item_id) === String(itemId));
    if (exists) setPkgSelectedItems(pkgSelectedItems.filter(s => String(s.item_id) !== String(itemId)));
    else setPkgSelectedItems([...pkgSelectedItems, { item_id: String(itemId), is_default: false }]);
  };
  const togglePkgDefault = (itemId) => {
    setPkgSelectedItems(pkgSelectedItems.map(s => String(s.item_id) === String(itemId) ? { ...s, is_default: !s.is_default } : s));
  };
  const getPkgRule = (catId) => pkgRules.find(r => String(r.category_id) === String(catId)) || { min_select: 0, max_select: 0 };
  const updatePkgRule = (catId, field, value) => {
    const idx = pkgRules.findIndex(r => String(r.category_id) === String(catId));
    if (idx >= 0) { const u = [...pkgRules]; u[idx] = { ...u[idx], [field]: Number(value) }; setPkgRules(u); }
    else setPkgRules([...pkgRules, { category_id: String(catId), min_select: 0, max_select: 0, [field]: Number(value) }]);
  };

  const inputStyle = { padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100%' };
  const btnPrimary = { padding: '0.5rem 1.2rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 };
  const btnOutline = { padding: '0.4rem 0.8rem', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', background: '#fff', color: '#374151' };
  const tabStyle = (active) => ({ padding: '0.6rem 1.5rem', border: 'none', borderBottom: active ? '3px solid #2563eb' : '3px solid transparent', background: 'none', cursor: 'pointer', fontWeight: active ? 700 : 500, color: active ? '#2563eb' : '#6b7280', fontSize: '0.9rem' });

  if (loading && !categories.length) return <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading...</div>;

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '1rem' }}>Menu Master</h2>

      {message && <div style={{ padding: '0.6rem 1rem', background: '#dcfce7', color: '#166534', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>{message}</div>}
      {error && <div style={{ padding: '0.6rem 1rem', background: '#fef2f2', color: '#991b1b', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem', display: 'flex', gap: '0.5rem' }}>
        <button style={tabStyle(tab === 'categories')} onClick={() => setTab('categories')}>Categories</button>
        <button style={tabStyle(tab === 'items')} onClick={() => setTab('items')}>Menu Items</button>
        <button style={tabStyle(tab === 'packages')} onClick={() => setTab('packages')}>Packages</button>
        <button style={tabStyle(tab === 'settings')} onClick={() => setTab('settings')}>Settings</button>
      </div>

      {/* Categories Tab */}
      {tab === 'categories' && (
        <div>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Category Name *</label>
              <input style={inputStyle} value={catForm.category_name} onChange={e => setCatForm({ ...catForm, category_name: e.target.value })} placeholder="e.g. Starter" />
            </div>
            <div style={{ width: '120px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Sort Order</label>
              <input style={inputStyle} type="number" value={catForm.display_order} onChange={e => setCatForm({ ...catForm, display_order: Number(e.target.value) })} />
            </div>
            <button style={btnPrimary} onClick={saveCategory}>{editingCatId ? 'Update' : 'Add Category'}</button>
            {editingCatId && <button style={btnOutline} onClick={() => { setEditingCatId(null); setCatForm({ category_name: '', display_order: 0 }); }}>Cancel</button>}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead><tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Category Name</th>
              <th style={{ padding: '0.6rem', textAlign: 'center', width: '100px' }}>Order</th>
              <th style={{ padding: '0.6rem', textAlign: 'center', width: '80px' }}>Active</th>
              <th style={{ padding: '0.6rem', textAlign: 'center', width: '150px' }}>Actions</th>
            </tr></thead>
            <tbody>{categories.map(c => (
              <tr key={c.category_id} style={{ borderBottom: '1px solid #f1f5f9', opacity: c.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '0.6rem' }}>{c.category_name}</td>
                <td style={{ padding: '0.6rem', textAlign: 'center' }}>{c.display_order}</td>
                <td style={{ padding: '0.6rem', textAlign: 'center' }}>{c.is_active ? '✓' : '✗'}</td>
                <td style={{ padding: '0.6rem', textAlign: 'center' }}>
                  <button style={{ ...btnOutline, marginRight: '0.3rem' }} onClick={() => { setEditingCatId(c.category_id); setCatForm({ category_name: c.category_name, display_order: c.display_order }); }}>Edit</button>
                  <button style={btnOutline} onClick={() => toggleActive('cat', c.category_id, c.is_active)}>{c.is_active ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
          {!categories.length && <p style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>No categories. Add your first menu category above.</p>}
        </div>
      )}

      {/* Menu Items Tab */}
      {tab === 'items' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <select style={{ ...inputStyle, width: '200px' }} value={filterCatId} onChange={e => setFilterCatId(e.target.value)}>
              <option value="">All Categories</option>
              {categories.filter(c => c.is_active).map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
            </select>
            <select style={{ ...inputStyle, width: '150px' }} value={filterFoodType} onChange={e => setFilterFoodType(e.target.value)}>
              <option value="">All Types</option>
              <option value="VEG">Veg</option>
              <option value="NON_VEG">Non-Veg</option>
            </select>
          </div>

          {/* Add/Edit Form */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '180px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Item Name *</label>
              <input style={inputStyle} value={itemForm.item_name} onChange={e => setItemForm({ ...itemForm, item_name: e.target.value })} placeholder="e.g. Paneer Tikka" />
            </div>
            <div style={{ width: '180px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Category *</label>
              <select style={inputStyle} value={itemForm.category_id} onChange={e => setItemForm({ ...itemForm, category_id: e.target.value })}>
                <option value="">Select...</option>
                {categories.filter(c => c.is_active).map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
              </select>
            </div>
            <div style={{ width: '120px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Type</label>
              <select style={inputStyle} value={itemForm.food_type} onChange={e => setItemForm({ ...itemForm, food_type: e.target.value })}>
                <option value="VEG">Veg</option>
                <option value="NON_VEG">Non-Veg</option>
              </select>
            </div>
            <div style={{ width: '110px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Price</label>
              <input style={inputStyle} type="number" value={itemForm.unit_price} onChange={e => setItemForm({ ...itemForm, unit_price: e.target.value })} placeholder="0" />
            </div>
            <button style={btnPrimary} onClick={saveItem}>{editingItemId ? 'Update' : 'Add Item'}</button>
            {editingItemId && <button style={btnOutline} onClick={() => { setEditingItemId(null); setItemForm({ item_name: '', category_id: '', food_type: 'VEG', unit_price: '' }); }}>Cancel</button>}
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead><tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Item Name</th>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Category</th>
              <th style={{ padding: '0.6rem', textAlign: 'center', width: '80px' }}>Type</th>
              <th style={{ padding: '0.6rem', textAlign: 'right', width: '100px' }}>Price</th>
              <th style={{ padding: '0.6rem', textAlign: 'center', width: '70px' }}>Active</th>
              <th style={{ padding: '0.6rem', textAlign: 'center', width: '150px' }}>Actions</th>
            </tr></thead>
            <tbody>{items.map(it => (
              <tr key={it.item_id} style={{ borderBottom: '1px solid #f1f5f9', opacity: it.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '0.6rem' }}>
                  <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: it.food_type === 'VEG' ? '#16a34a' : '#dc2626', marginRight: '0.5rem' }}></span>
                  {it.item_name}
                </td>
                <td style={{ padding: '0.6rem', color: '#6b7280' }}>{it.category_name}</td>
                <td style={{ padding: '0.6rem', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '10px', background: it.food_type === 'VEG' ? '#dcfce7' : '#fef2f2', color: it.food_type === 'VEG' ? '#166534' : '#991b1b' }}>{it.food_type === 'VEG' ? 'Veg' : 'Non-Veg'}</span>
                </td>
                <td style={{ padding: '0.6rem', textAlign: 'right' }}>{it.unit_price > 0 ? `₹${Number(it.unit_price).toLocaleString('en-IN')}` : '-'}</td>
                <td style={{ padding: '0.6rem', textAlign: 'center' }}>{it.is_active ? '✓' : '✗'}</td>
                <td style={{ padding: '0.6rem', textAlign: 'center' }}>
                  <button style={{ ...btnOutline, marginRight: '0.3rem' }} onClick={() => { setEditingItemId(it.item_id); setItemForm({ item_name: it.item_name, category_id: String(it.category_id), food_type: it.food_type, unit_price: it.unit_price || '' }); }}>Edit</button>
                  <button style={btnOutline} onClick={() => toggleActive('item', it.item_id, it.is_active)}>{it.is_active ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
          {!items.length && <p style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>No menu items. Add your first item above.</p>}
        </div>
      )}

      {/* Packages Tab */}
      {tab === 'packages' && (
        <div>
          {/* Package Builder */}
          {showPkgBuilder ? (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '1rem' }}>{editingPkgId ? 'Edit Package' : 'Create Package'}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Package Name *</label>
                  <input style={inputStyle} value={pkgForm.package_name} onChange={e => setPkgForm({ ...pkgForm, package_name: e.target.value, package_code: pkgForm.package_code || e.target.value.toUpperCase().replace(/\s+/g, '_') })} placeholder="e.g. Veg Silver" /></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Code *</label>
                  <input style={inputStyle} value={pkgForm.package_code} onChange={e => setPkgForm({ ...pkgForm, package_code: e.target.value.toUpperCase().replace(/\s+/g, '_') })} /></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Menu Type *</label>
                  <select style={inputStyle} value={pkgForm.menu_type} onChange={e => setPkgForm({ ...pkgForm, menu_type: e.target.value })}>
                    <option value="VEG">Veg</option><option value="NON_VEG">Non-Veg</option><option value="MIXED">Mixed</option>
                  </select></div>
                <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Price/Pax *</label>
                  <input style={inputStyle} type="number" value={pkgForm.base_price_per_pax} onChange={e => setPkgForm({ ...pkgForm, base_price_per_pax: e.target.value })} placeholder="650" /></div>
              </div>
              <div style={{ marginBottom: '1rem' }}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Description</label>
                <textarea style={{ ...inputStyle, minHeight: '50px' }} value={pkgForm.description} onChange={e => setPkgForm({ ...pkgForm, description: e.target.value })} /></div>

              <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>Selection Rules & Items</h4>
              {categories.filter(c => c.is_active).map(cat => {
                const catItems = pkgItemsByCategory[cat.category_id] || [];
                if (!catItems.length) return null;
                const rule = getPkgRule(cat.category_id);
                const selectedCount = pkgSelectedItems.filter(s => catItems.some(ci => String(ci.item_id) === String(s.item_id))).length;
                return (
                  <div key={cat.category_id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem', flexWrap: 'wrap', gap: '0.4rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{cat.category_name}</span>
                      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.78rem' }}>
                        <span>Min:</span><input type="number" min="0" style={{ ...inputStyle, width: '55px', padding: '0.25rem 0.4rem' }} value={rule.min_select} onChange={e => updatePkgRule(cat.category_id, 'min_select', e.target.value)} />
                        <span>Max:</span><input type="number" min="0" style={{ ...inputStyle, width: '55px', padding: '0.25rem 0.4rem' }} value={rule.max_select} onChange={e => updatePkgRule(cat.category_id, 'max_select', e.target.value)} />
                        <span style={{ color: '#6b7280' }}>({selectedCount} selected)</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                      {catItems.map(item => {
                        const isSel = pkgSelectedItems.some(s => String(s.item_id) === String(item.item_id));
                        const isDef = pkgSelectedItems.find(s => String(s.item_id) === String(item.item_id))?.is_default;
                        return (
                          <div key={item.item_id} style={{
                            display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.65rem',
                            border: `1px solid ${isSel ? '#2563eb' : '#d1d5db'}`, borderRadius: '20px',
                            background: isSel ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '0.78rem'
                          }} onClick={() => togglePkgItem(item.item_id)}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: item.food_type === 'VEG' ? '#16a34a' : '#dc2626' }}></span>
                            <span>{item.item_name}</span>
                            {isSel && <span onClick={e => { e.stopPropagation(); togglePkgDefault(item.item_id); }}
                              style={{ marginLeft: '0.15rem', fontSize: '0.68rem', padding: '1px 4px', borderRadius: '8px', background: isDef ? '#fbbf24' : '#e5e7eb', color: isDef ? '#78350f' : '#6b7280', cursor: 'pointer' }}>{isDef ? 'Default' : 'Opt'}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
                <button style={btnOutline} onClick={resetPkgForm}>Cancel</button>
                <button style={{ ...btnPrimary, background: '#16a34a' }} onClick={savePackage}>{editingPkgId ? 'Update Package' : 'Save Package'}</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button style={btnPrimary} onClick={() => { resetPkgForm(); setShowPkgBuilder(true); }}>+ New Package</button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead><tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Package Name</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Code</th>
                  <th style={{ padding: '0.6rem', textAlign: 'center', width: '90px' }}>Menu Type</th>
                  <th style={{ padding: '0.6rem', textAlign: 'right', width: '110px' }}>Price/Pax</th>
                  <th style={{ padding: '0.6rem', textAlign: 'center', width: '70px' }}>Active</th>
                  <th style={{ padding: '0.6rem', textAlign: 'center', width: '150px' }}>Actions</th>
                </tr></thead>
                <tbody>{packages.map(p => (
                  <tr key={p.package_id} style={{ borderBottom: '1px solid #f1f5f9', opacity: p.is_active ? 1 : 0.5 }}>
                    <td style={{ padding: '0.6rem', fontWeight: 600 }}>{p.package_name}</td>
                    <td style={{ padding: '0.6rem', color: '#6b7280', fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.package_code}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'center' }}>
                      <span style={{ fontSize: '0.73rem', padding: '2px 8px', borderRadius: '10px',
                        background: p.menu_type === 'VEG' ? '#dcfce7' : p.menu_type === 'NON_VEG' ? '#fef2f2' : '#fef9c3',
                        color: p.menu_type === 'VEG' ? '#166534' : p.menu_type === 'NON_VEG' ? '#991b1b' : '#854d0e' }}>
                        {p.menu_type === 'NON_VEG' ? 'Non-Veg' : p.menu_type === 'MIXED' ? 'Mixed' : 'Veg'}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem', textAlign: 'right' }}>₹{Number(p.base_price_per_pax).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'center' }}>{p.is_active ? '✓' : '✗'}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'center' }}>
                      <button style={{ ...btnOutline, marginRight: '0.3rem' }} onClick={() => editPackage(p.package_id)}>Edit</button>
                      <button style={btnOutline} onClick={() => toggleActive('pkg', p.package_id, p.is_active)}>{p.is_active ? 'Deactivate' : 'Activate'}</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
              {!packages.length && <p style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>No packages. Click "+ New Package" to create one.</p>}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {tab === 'settings' && (
        <div style={{ maxWidth: '500px' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '1rem' }}>Banquet Quotation Settings</h3>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Billing Profile</label>
            <select style={inputStyle} value={configForm.billing_profile_id} onChange={e => setConfigForm({ ...configForm, billing_profile_id: e.target.value })}>
              <option value="">Select billing profile...</option>
              {billingProfiles.map(bp => <option key={bp.billing_profile_id} value={bp.billing_profile_id}>{bp.profile_name} — {bp.billing_company_name || bp.profile_code}</option>)}
            </select>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.3rem' }}>Hotel billing info shown on quotation header (name, address, GSTIN).</p>
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem' }}>Tax Profile (GST)</label>
            <select style={inputStyle} value={configForm.tax_id} onChange={e => setConfigForm({ ...configForm, tax_id: e.target.value })}>
              <option value="">Select tax profile...</option>
              {taxOptions.map(t => <option key={t.id} value={t.id}>{t.tax_name} — {t.tax_percent}%</option>)}
            </select>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.3rem' }}>Default tax rate applied to all banquet quotations.</p>
          </div>
          <button style={btnPrimary} onClick={saveConfig} disabled={configSaving}>{configSaving ? 'Saving...' : 'Save Settings'}</button>
        </div>
      )}
    </div>
  );
}

export default BanquetMenuMaster;
